interface CacheItem {
  response: string;
  timestamp: number;
  confidence: number;
}

class ResponseCache {
  private cache: Map<string, CacheItem>;
  private readonly maxSize: number;
  private readonly ttl: number; // Time to live in milliseconds

  constructor(maxSize = 100, ttlMinutes = 60) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.ttl = ttlMinutes * 60 * 1000;
  }

  private generateKey(message: string): string {
    // Normaliza a mensagem para melhorar as chances de cache hit
    return message.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  private calculateConfidence(input: string, cached: string): number {
    const inputWords = new Set(input.toLowerCase().split(' '));
    const cachedWords = new Set(cached.toLowerCase().split(' '));
    const intersection = new Set([...inputWords].filter(x => cachedWords.has(x)));
    return intersection.size / Math.max(inputWords.size, cachedWords.size);
  }

  get(message: string): { response: string; confidence: number } | null {
    const key = this.generateKey(message);
    const item = this.cache.get(key);

    if (!item) {
      // Procura por respostas similares
      let bestMatch: { response: string; confidence: number } | null = null;
      this.cache.forEach((cached, cachedKey) => {
        const confidence = this.calculateConfidence(key, cachedKey);
        if (confidence > 0.8 && (!bestMatch || confidence > bestMatch.confidence)) {
          bestMatch = { response: cached.response, confidence };
        }
      });
      return bestMatch;
    }

    // Verifica se o item expirou
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return { response: item.response, confidence: 1 };
  }

  set(message: string, response: string): void {
    const key = this.generateKey(message);

    // Remove o item mais antigo se o cache estiver cheio
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      response,
      timestamp: Date.now(),
      confidence: 1
    });
  }

  clear(): void {
    this.cache.clear();
  }

  // Remove itens expirados
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > this.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

export const responseCache = new ResponseCache();
